import { Box, Slider, Typography } from '@mui/material';
import type { KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import {
    DOSE_OPACITY_STEP,
    doseOpacityOnStep,
    doseOpacityState,
} from '~/state/atoms/dose/doseAtoms';
import { CHROME_BG_HOVER, consoleSliderSx } from '~/viewportGrid';

/** The opacity a key moves to from `opacity`, or undefined for another key. */
function opacityForKey(key: string, opacity: number): number | undefined {
    switch (key) {
        case 'ArrowLeft':
            return opacity - DOSE_OPACITY_STEP;
        case 'ArrowRight':
            return opacity + DOSE_OPACITY_STEP;
        case 'Home':
            return 0;
        case 'End':
            return 1;
        default:
            return undefined;
    }
}

/**
 * The dose opacity as a row of a console menu: its name, a slider and the
 * percent. The row itself is the keyboard slider, so the menu's arrow keys
 * reach it like any other row: Left and Right step it, Home and End go to
 * its ends, and Up and Down move on through the menu. The opacity is the
 * one display state that every drawn dose follows.
 */
export default function DoseOpacityMenuRow() {
    const intl = useIntl();
    const [opacity, setOpacity] = useRecoilState(doseOpacityState);
    const percent = Math.round(opacity * 100);
    const name = intl.formatMessage({ id: 'dose.opacity' });
    const valueText = intl.formatMessage(
        { id: 'dose.opacityPercent' },
        { value: percent }
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        const next = opacityForKey(event.key, opacity);
        if (next === undefined) {
            return;
        }
        // The menu would otherwise take Home and End to its first and last
        // row.
        event.preventDefault();
        event.stopPropagation();
        setOpacity(doseOpacityOnStep(next));
    };

    return (
        <Box
            role="slider"
            tabIndex={-1}
            aria-label={name}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-valuetext={valueText}
            onKeyDown={handleKeyDown}
            data-testid="row-dose-opacity"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 28,
                px: 1,
                outline: 'none',
                '&:focus': { bgcolor: CHROME_BG_HOVER },
            }}
        >
            <Typography fontSize={12} flex={1}>
                {name}
            </Typography>
            <Slider
                size="small"
                value={percent}
                min={0}
                max={100}
                step={DOSE_OPACITY_STEP * 100}
                onChange={(_, value) =>
                    setOpacity(doseOpacityOnStep((value as number) / 100))
                }
                // The row is the keyboard slider; this one takes the pointer.
                slotProps={{ input: { tabIndex: -1, 'aria-hidden': true } }}
                sx={consoleSliderSx(96)}
            />
            <Typography
                variant="monoMeta"
                sx={{ minWidth: '5ch', textAlign: 'end' }}
            >
                {valueText}
            </Typography>
        </Box>
    );
}
